import React from 'react';
import { Link, Redirect, Switch } from 'react-router-dom';
import useMain from '../../hooks/useMain';
import http from '../../services/httpService';
import AccessDenied from '../accessDenied';
import ComingSoon from '../comingSoon';
import InfoBox from '../common/infoBox';
import ProtectedRoute from '../common/protectedRoute';
import HeaderImage from './ingredients/headerImage';
import MainPanelHeader from './mainPanelHeader';
import Summary from './summary';
import tabs from './tabs.json';
import Preview from './tabs/preview/preview';

const MainPanel = () => {
	const { userData, uiInfo, mediaQuery, initData } = useMain();
	const { emailStatus, terminationRequest, isEmeraldLeader } = userData;
	const isEmailConfirmed = emailStatus === 'Active';
	const isTerminating = terminationRequest.isActive;
	const isTerminated = terminationRequest.isApproved;
	const user = http.getCurrentUser();
	const userIsLeader = user.isAdmin || isEmeraldLeader;

	const routes = getComponents(isEmailConfirmed, isTerminating, isTerminated, userIsLeader);
	const redirects = [];
	for (const i in tabs) {
		const tab = tabs[i];
		const hasChildren = tab.children && tab.children.length > 0;
		if (!tab.toBeShown || !hasChildren) continue;
		const firstActiveChildIndex = tab.children.findIndex((c) => c.toBeShown === true);
		redirects.push(
			<Redirect
				key={i}
				from={`/dashboard/${tab.id}`}
				to={`/dashboard/${tab.id}/${tab.children[firstActiveChildIndex].id}`}
			/>
		);
	}

	return (
		<Switch>
			{routes.map(({ path, Component, tab, crumbs, name }, key) => (
				<ProtectedRoute
					exact
					path={path}
					key={key}
					name='test'
					render={(props) => (
						<>
							<MainPanelHeader
								crumbs={crumbs}
								name={name}
							/>
							<div className='d-block d-md-none'>
								<Summary />
							</div>
							<main
								id='contentsCard'
								className='card contentsCard mt-4 mb-4 mb-md-0'>
								<div
									id='contentsBody'
									className='card-body contentsBody p-lg-4 my-md-2 me-md-2'
									style={
										mediaQuery === 'xs' || mediaQuery === 'sm'
											? null
											: {
													overflowX: 'hidden',
													overflowY: 'scroll',
													height: `calc(100vh - ${uiInfo.mainPanel}px)`,
											  }
									}>
									{getUserAlerts(userData, initData)}
									<HeaderImage tab={tab} />
									<Component
										{...props}
										tab={tab}
									/>
								</div>
							</main>
						</>
					)}
				/>
			))}
			{redirects}
			<Redirect
				from='/dashboard'
				to='/dashboard/home'
			/>
			<Redirect to='/notFound' />
		</Switch>
	);
};

const getComponents = (isEmailConfirmed, isTerminating, isTerminated, userIsLeader) => {
	const routes = [];
	for (let i = 0; i < tabs.length; i++) {
		const tab = tabs[i];
		if (!tab.toBeShown) continue;
		const hasChildren = tab.children && tab.children.length > 0;
		if (!hasChildren) {
			const route = {
				tab,
				path: `/dashboard/${tab.id}`,
				name: tab.name,
				crumbs: [tab.name],
				Component: !tab.active
					? () => ComingSoon({ withLink: false })
					: (tab.termination && (isTerminating || isTerminated)) ||
					  (tab.isLeader && !userIsLeader)
					? () => AccessDenied({ withLink: false })
					: tab.isPreview
					? () => Preview({ tab })
					: require(`./tabs/${tab.id}`).default,
			};
			routes.push(route);
		} else {
			for (const j in tab.children) {
				const child = tab.children[j];
				if (!child.toBeShown) continue;
				const route = {
					tab: child,
					path: `/dashboard/${tab.id}/${child.id}`,
					name: child.name,
					crumbs: [tab.name, child.name],
					Component: !child.active
						? () => ComingSoon({ withLink: false })
						: (child.termination && (isTerminating || isTerminated)) ||
						  (child.isLeader && !userIsLeader)
						? () => AccessDenied({ withLink: false })
						: require(`./tabs/${tab.id}/${child.template || child.id}`).default,
				};
				routes.push(route);
			}
		}
	}
	return routes;
};

const getUserAlerts = (user, initData) => {
	return (
		<>
			{user.terminationRequest.isApproved && (
				<InfoBox
					extraClass='alertBox'
					type='danger'
					content='This account is Early Terminated. some features are not active.'
				/>
			)}
			{user.terminationRequest.isActive && (
				<InfoBox
					extraClass='alertBox'
					type='danger'
					content='Early Termination is in progress. some features are not active.'
				/>
			)}
			{initData.kycRequest &&
				user.verificationLevel === 'level_0' &&
				user.verificationRequest.status !== 'pending' && (
					<InfoBox
						extraClass='alertBox'
						type='danger'
						icon={<i className='me-3 fa-2x fas fa-id-card'></i>}
						content={
							<Link
								to='/dashboard/myProfile/kyc'
								style={{
									textDecoration: 'none',
									color: 'unset',
								}}>
								Verify Your Identity: KYC Process Required.
							</Link>
						}
					/>
				)}
		</>
	);
};

export default MainPanel;
